import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatINR } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Banknote, Smartphone, CreditCard, Building2, Loader2, Receipt, ScanLine } from "lucide-react";
import { toast } from "sonner";
import UpiQrDialog from "./UpiQrDialog";

export const PAYMENT_METHODS = [
  { key: "cash", label: "Cash", icon: Banknote, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  { key: "upi", label: "UPI", icon: Smartphone, color: "text-violet-600 bg-violet-50 border-violet-200" },
  { key: "card", label: "Card", icon: CreditCard, color: "text-blue-600 bg-blue-50 border-blue-200" },
  { key: "bank_transfer", label: "Bank", icon: Building2, color: "text-slate-700 bg-slate-50 border-slate-200" },
];

export function MethodPicker({ value, onChange, testidPrefix = "pay-method" }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {PAYMENT_METHODS.map((m) => {
        const active = value === m.key;
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => onChange(m.key)}
            data-testid={`${testidPrefix}-${m.key}`}
            className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
              active
                ? `${m.color} border-current`
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            <m.icon className="w-5 h-5" />
            <span className="text-xs font-medium">{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Quick Bill / Mark Paid dialog. Used by:
 *   - Patient Save & Bill / Save & Mark Paid
 *   - Patient profile "Generate Bill" / "Mark Paid"
 *   - Appointment / Queue card quick actions
 *   - Dashboard "Generate Bill" / "Mark Paid"
 *
 * Props:
 *   - open, onOpenChange
 *   - patient: {patient_id, name, phone}
 *   - mode: "bill" (just create invoice) | "paid" (create + mark paid)
 *   - defaultAmount: number (from clinic default_consultation_fee)
 *   - onCompleted: (invoice) => void  // parent decides what to do next (e.g. show follow-up)
 *   - upiVpa, upiPayeeName: enable "Generate + Show UPI QR" CTA when present
 */
export default function QuickBillDialog({
  open, onOpenChange, patient, mode = "bill", defaultAmount, onCompleted,
  upiVpa, upiPayeeName,
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);
  const [submittingQr, setSubmittingQr] = useState(false);
  const [error, setError] = useState("");
  const [qrInvoice, setQrInvoice] = useState(null);

  useEffect(() => {
    if (open) {
      setAmount(defaultAmount != null ? String(defaultAmount) : "500");
      setMethod("cash");
      setError("");
    }
  }, [open, defaultAmount]);

  const handleSubmit = async (e, opts = {}) => {
    e?.preventDefault?.();
    if (!patient?.patient_id) { setError("No patient selected"); return; }
    const amt = parseFloat(amount);
    if (!(amt > 0)) { setError("Enter a valid amount"); return; }
    const showQrAfter = !!opts.showQr;
    if (showQrAfter) setSubmittingQr(true); else setSubmitting(true);
    setError("");
    try {
      const payload = {
        patient_id: patient.patient_id,
        amount: amt,
      };
      if (mode === "paid") {
        payload.status = "paid";
        payload.payment_method = method;
      }
      const { data } = await api.post("/invoices", payload);
      if (showQrAfter) {
        toast.success("Bill generated · scan QR to receive payment");
        setQrInvoice(data);
        // keep parent informed of creation; parent may refresh lists
        onCompleted && onCompleted(data, { keepOpen: true });
        return;
      }
      toast.success(mode === "paid" ? `${formatINR(amt)} received · ${method.toUpperCase()}` : "Invoice created");
      onOpenChange(false);
      onCompleted && onCompleted(data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
      setSubmittingQr(false);
    }
  };

  const isPaid = mode === "paid";
  const upiAvailable = !!(upiVpa && upiVpa.includes("@"));

  return (
    <>
      <Dialog open={open && !qrInvoice} onOpenChange={(o) => { onOpenChange(o); if (!o) setQrInvoice(null); }}>
        <DialogContent data-testid="quickbill-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <Receipt className="w-5 h-5 text-blue-600" />
              {isPaid ? "Collect payment" : "Generate bill"}
            </DialogTitle>
            <DialogDescription>
              {patient?.name ? `For ${patient.name}` : "Quick invoice"}
              {defaultAmount != null && (
                <span className="text-slate-400"> · Default ₹{defaultAmount}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input
                type="number"
                min="0"
                step="50"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
                data-testid="quickbill-amount-input"
                className="mt-1.5 h-11 text-lg font-semibold"
              />
            </div>
            {isPaid && (
              <div>
                <Label className="text-xs">Payment method</Label>
                <div className="mt-2" data-testid="quickbill-method-picker">
                  <MethodPicker value={method} onChange={setMethod} />
                </div>
              </div>
            )}
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="quickbill-error">
                {error}
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
              {!isPaid && upiAvailable && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={(e) => handleSubmit(e, { showQr: true })}
                  disabled={submittingQr || submitting || !amount}
                  className="border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-700"
                  data-testid="quickbill-show-upi-qr-btn"
                >
                  {submittingQr ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ScanLine className="w-4 h-4 mr-2" />}
                  Show UPI QR
                </Button>
              )}
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={submitting || submittingQr || !amount}
                className={isPaid ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}
                data-testid="quickbill-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isPaid ? `Mark paid · ${formatINR(parseFloat(amount) || 0)}` : "Generate bill")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <UpiQrDialog
        open={!!qrInvoice}
        onOpenChange={(o) => {
          if (!o) {
            setQrInvoice(null);
            onOpenChange(false);
          }
        }}
        invoice={qrInvoice}
        upiVpa={upiVpa}
        upiPayeeName={upiPayeeName}
        onPaid={(updated) => {
          setQrInvoice(null);
          onOpenChange(false);
          onCompleted && onCompleted(updated || qrInvoice, { paid: true });
        }}
      />
    </>
  );
}
